// frontend/src/components/PokeFriendsModal.tsx
// Poke Friends to Share modal (#295) with friend search (#302)

import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Checkbox, TextField, Chip,
    Avatar, CircularProgress, Alert, Divider,
    InputAdornment,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import SearchIcon from '@mui/icons-material/Search';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev';

interface Friend {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    status: string;
}

interface PokeFriendsModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (count: number) => void;
    preSelectedFriendId?: string;
}

const PokeFriendsModal: React.FC<PokeFriendsModalProps> = ({ open, onClose, onSuccess, preSelectedFriendId }) => {
    const [friends, setFriends] = useState<Friend[]>([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [message, setMessage] = useState('');
    const [search, setSearch] = useState('');
    const [newPokeName, setNewPokeName] = useState('');
    const [newPokeEmail, setNewPokeEmail] = useState('');
    const [newPokeEntries, setNewPokeEntries] = useState<{name: string; email: string}[]>([]);

    useEffect(() => {
        if (!open) return;
        setSelectedIds(preSelectedFriendId ? [preSelectedFriendId] : []);
        setNewPokeName('');
        setNewPokeEmail('');
        setNewPokeEntries([]);
        setMessage('');
        setError('');
        setSearch('');
        fetchFriends();
    }, [open, preSelectedFriendId]);

    const fetchFriends = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('idToken');
            const res = await fetch(`${API_URL}/friends/list`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data.success && data.friends) {
                setFriends(data.friends.filter((f: Friend) => f.status === 'active'));
            }
        } catch {
            setError('Failed to load friends');
        } finally {
            setLoading(false);
        }
    };

    const filteredFriends = friends.filter(f => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return f.name?.toLowerCase().includes(q) || f.email?.toLowerCase().includes(q);
    });

    const toggleFriend = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSend = async () => {
        if (selectedIds.length === 0 && newPokeEntries.length === 0) return;
        if (!message.trim()) return;
        setSending(true);
        setError('');
        try {
            const token = localStorage.getItem('idToken');
            // Invite new emails as friends first
            for (const entry of newPokeEntries) {
                await fetch(`${API_URL}/friends/invite`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ email: entry.email, name: entry.name, message: message.trim() }),
                });
            }
            if (selectedIds.length === 0) {
                onSuccess(newPokeEntries.length);
                onClose();
                return;
            }
            const res = await fetch(`${API_URL}/friends/poke`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ friend_ids: selectedIds, message: message.trim() }),
            });
            const data = await res.json();
            if (data.success) {
                onSuccess(data.sent);
                onClose();
            } else {
                setError(data.error || 'Failed to send poke');
            }
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CampaignIcon sx={{ color: '#2D6E6A' }} />
                Poke Friends to Share
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Send a friendly nudge to your friends encouraging them to share their travel memories.
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Your message"
                    placeholder="Hey! Don't forget to share your photos from our trip..."
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    helperText={`${message.length}/500`}
                    sx={{ mb: 2 }}
                />

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search friends by name or email..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Select friends ({selectedIds.length} selected)
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} sx={{ color: '#2D6E6A' }} />
                    </Box>
                ) : filteredFriends.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        {search ? 'No friends match your search.' : 'No active friends found.'}
                    </Typography>
                ) : (
                    <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: 2 }}>
                        {filteredFriends.map((friend, idx) => (
                            <Box key={friend.id}>
                                <Box
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        px: 2, py: 1.5, cursor: 'pointer',
                                        '&:hover': { bgcolor: '#F5F5F5' },
                                    }}
                                    onClick={() => toggleFriend(friend.id)}
                                >
                                    <Checkbox
                                        checked={selectedIds.includes(friend.id)}
                                        sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' }, p: 0 }}
                                    />
                                    <Avatar
                                        src={friend.avatar}
                                        sx={{ width: 32, height: 32, bgcolor: '#2D6E6A', fontSize: '0.85rem' }}
                                    >
                                        {friend.name?.[0]?.toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>{friend.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{friend.email}</Typography>
                                    </Box>
                                </Box>
                                {idx < filteredFriends.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Poke someone new to Percoro
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    They'll get a friend invite with your poke message. Once they join, they'll appear in your Friends list.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField size="small" label="Their name" value={newPokeName} onChange={(e) => setNewPokeName(e.target.value)} sx={{ flex: 1 }} />
                    <TextField size="small" label="Email" type="email" value={newPokeEmail} onChange={(e) => setNewPokeEmail(e.target.value)} sx={{ flex: 1.5 }} />
                    <Button size="small" variant="outlined" disabled={!newPokeName.trim() || !newPokeEmail.trim() || !newPokeEmail.includes('@')}
                        onClick={() => { setNewPokeEntries(prev => [...prev, { name: newPokeName.trim(), email: newPokeEmail.trim() }]); setNewPokeName(''); setNewPokeEmail(''); }}
                        sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', textTransform: 'none' }}>
                        Add
                    </Button>
                </Box>
                {newPokeEntries.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {newPokeEntries.map((e, i) => (
                            <Chip key={i} label={e.name} size="small" onDelete={() => setNewPokeEntries(prev => prev.filter((_, idx) => idx !== i))}
                                sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A' }} />
                        ))}
                    </Box>
                )}
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending || (selectedIds.length === 0 && newPokeEntries.length === 0) || !message.trim()}
                    sx={{ bgcolor: '#2D6E6A', textTransform: 'none', borderRadius: '24px', px: 3, '&:hover': { bgcolor: '#009688' } }}
                >
                    {sending ? <CircularProgress size={20} color="inherit" /> : `Send Poke${(selectedIds.length + newPokeEntries.length) > 0 ? ` (${selectedIds.length + newPokeEntries.length})` : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PokeFriendsModal;
